import { Geodesic } from "geographiclib-geodesic";
import type { LatLng } from "./types";

function normalizeLongitude(lng: number): number {
  const normalized = ((lng + 180) % 360 + 360) % 360 - 180;
  return Object.is(normalized, -0) ? 0 : normalized;
}

export class LocalAeqdProjection {
  readonly center: LatLng;

  constructor(center: LatLng) {
    this.center = { lat: center.lat, lng: normalizeLongitude(center.lng) };
  }

  /**
   * Ellipsoidal azimuthal-equidistant coordinates. GeographicLib provides the WGS84 geodesic
   * distance and forward azimuth; resolving that vector into east/north preserves true distance
   * from the selected center, including across the antimeridian.
   */
  forward(point: LatLng): { x: number; north: number } {
    const result = Geodesic.WGS84.Inverse(this.center.lat, this.center.lng, point.lat, normalizeLongitude(point.lng));
    const distance = result.s12 ?? 0;
    const azimuth = ((result.azi1 ?? 0) * Math.PI) / 180;
    return { x: Math.sin(azimuth) * distance, north: Math.cos(azimuth) * distance };
  }

  inverse(x: number, north: number): LatLng {
    const distance = Math.hypot(x, north);
    if (distance === 0) return { ...this.center };
    const azimuth = (Math.atan2(x, north) * 180) / Math.PI;
    const result = Geodesic.WGS84.Direct(this.center.lat, this.center.lng, azimuth, distance);
    if (result.lat2 === undefined || result.lon2 === undefined) throw new Error("AEQD inverse projection failed.");
    return { lat: result.lat2, lng: normalizeLongitude(result.lon2) };
  }

  toThree(point: LatLng, y = 0): [number, number, number] {
    const { x, north } = this.forward(point);
    return [x, y, -north];
  }

  contains(point: LatLng, radiusMeters: number, toleranceMeters = 0.001): boolean {
    const { x, north } = this.forward(point);
    return Math.hypot(x, north) <= radiusMeters + toleranceMeters;
  }
}
